import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type {
  SelectContentProps,
  SelectItemProps,
  SelectTriggerProps,
} from "@kobalte/core/select";
import {
  Select as SelectPrimitive,
  useSelectContext,
} from "@kobalte/core/select";
import type { ParentProps, ValidComponent } from "solid-js";
import { splitProps } from "solid-js";
import { cn } from "@/libs/cn";
import { FocusGuard } from "./layer-focus";

export const Select = SelectPrimitive;
export const SelectValue = SelectPrimitive.Value;
export const SelectDescription = SelectPrimitive.Description;
export const SelectErrorMessage = SelectPrimitive.ErrorMessage;
export const SelectItemDescription = SelectPrimitive.ItemDescription;
export const SelectHiddenSelect = SelectPrimitive.HiddenSelect;
export const SelectSection = SelectPrimitive.Section;

export type SelectProps = typeof SelectPrimitive;

type selectTriggerProps<T extends ValidComponent = "button"> = ParentProps<
  SelectTriggerProps<T> & { class?: string }
>;

export const SelectTrigger = <T extends ValidComponent = "button">(
  props: PolymorphicProps<T, selectTriggerProps<T>>,
) => {
  const [local, rest] = splitProps(props as selectTriggerProps, [
    "class",
    "children",
  ]);

  return (
    <SelectPrimitive.Trigger
      // `bg-card`, not `bg-transparent`: the app background is #f1f1f1, so a
      // transparent trigger reads as grey-on-grey next to an `Input` — which
      // has always been `bg-card`. A control that takes a value should look
      // like the other controls that take a value.
      class={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-card px-3 py-2 text-sm shadow-xs ring-offset-background transition-shadow placeholder:text-muted-foreground focus:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        local.class,
      )}
      {...rest}
    >
      {local.children}
      <SelectPrimitive.Icon
        as="svg"
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        class="flex size-4 items-center justify-center opacity-50"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m8 9l4-4l4 4m0 6l-4 4l-4-4"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
};

type selectContentProps<T extends ValidComponent = "div"> =
  SelectContentProps<T> & {
    class?: string;
  };

/**
 * The listbox — not the content — carries both the viewport cap and the
 * scrolling.
 *
 * The cap is what keeps a long list on screen: floating-ui's `bestFit` flip
 * picks the roomier side, but when the list is taller than the space above
 * *and* below the trigger, flipping cannot help and the list runs past the
 * fold. The scrolling has to sit on the same element because Kobalte scrolls
 * the keyboard-focused option with `scrollIntoView` on its own listbox ref — a
 * scroll container anywhere else leaves arrow/End navigation focusing options
 * the user cannot see.
 *
 * Capping here rather than on the content is what lets a call site keep its own
 * `max-h-*`: `twMerge` would drop a base cap declared on the same element, so
 * instead the two bounds sit on different elements and compose to the smaller
 * one through the flex chain. `min-h-0` is what lets the listbox shrink below
 * its intrinsic height when the content's cap is the tighter of the two.
 *
 * The variable is `--kb-popper-*`, not `--kb-select-*`: Kobalte sets it on the
 * positioner and only aliases the transform-origin per component.
 *
 * `data-kb-top-layer` is what keeps the options readable inside a dialog. The
 * list is portalled to `body`, and a modal dialog marks `aria-hidden` every node
 * it does not contain, including the ones added while it is open — so screen
 * readers, and role queries, found no options at all. The attribute is Kobalte's
 * own "top layer" marker (the one `Toast.Region`'s `topLayer` sets; Select has
 * no prop for it up to 0.13.14): the hide-outside walk skips it, and focus traps
 * and outside-click checks count it as inside. All three already hold for an
 * open select, which closes on any outside interaction and is therefore always
 * the topmost layer. Portalling into the dialog instead would clip the list:
 * the dialog scrolls and is transformed. `select.test.tsx` fails if a Kobalte
 * upgrade renames the attribute.
 *
 * The `FocusGuard`s make Tab from the open list behave like a native select's:
 * on to the control after the trigger, or back to the trigger with Shift+Tab.
 * Kobalte's listbox moves focus to its own last option, or to itself, and lets
 * the browser take the next step, which from a list portalled to the end of
 * `body` left the page; focus came back to the trigger only after the exit
 * animation.
 */
export const SelectContent = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, selectContentProps<T>>,
) => {
  const [local, rest] = splitProps(props as selectContentProps, ["class"]);
  const context = useSelectContext();

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        class={cn(
          "relative z-50 flex min-w-32 flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0 data-closed:zoom-out-95 data-expanded:zoom-in-95 origin-(--kb-select-content-transform-origin)",
          local.class,
        )}
        data-kb-top-layer=""
        {...rest}
      >
        <FocusGuard edge="start" reference={context.triggerRef} />
        <SelectPrimitive.Listbox class="max-h-(--kb-popper-content-available-height) min-h-0 overflow-y-auto p-1 focus-visible:outline-hidden" />
        <FocusGuard edge="end" reference={context.triggerRef} />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

type selectItemProps<T extends ValidComponent = "li"> = ParentProps<
  SelectItemProps<T> & { class?: string }
>;

export const SelectItem = <T extends ValidComponent = "li">(
  props: PolymorphicProps<T, selectItemProps<T>>,
) => {
  const [local, rest] = splitProps(props as selectItemProps, [
    "class",
    "children",
  ]);

  return (
    <SelectPrimitive.Item
      class={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
        local.class,
      )}
      {...rest}
    >
      <SelectPrimitive.ItemIndicator class="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4"
          viewBox="0 0 24 24"
        >
          <title>Checked</title>
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m5 12l5 5L20 7"
          />
        </svg>
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemLabel>{local.children}</SelectPrimitive.ItemLabel>
    </SelectPrimitive.Item>
  );
};
